import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MapPin, CalendarDays, Building2, ExternalLink, Search, Globe, Loader2, RefreshCw } from "lucide-react";
import { tr } from "date-fns/locale";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [scrapingNews, setScrapingNews] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [loadingWebsite, setLoadingWebsite] = useState({});

  const fetchEvents = useCallback(async (search, country) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (country) params.set("country", country);
      params.set("limit", "500");
      const res = await axios.get(`${API}/events?${params.toString()}`);
      setEvents(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/events/countries`);
      setCountries(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchEvents("", "");
    fetchCountries();
  }, [fetchEvents]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents(searchQuery, selectedCountry);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    fetchEvents(searchQuery, country);
  };

  const handleRefresh = async () => {
    setScraping(true);
    try {
      const res = await axios.post(`${API}/scrape-events`);
      toast.success(res.data.message);
      fetchEvents("", "");
      fetchCountries();
    } catch (e) {
      toast.error("Etkinlikler güncellenirken bir hata oluştu.");
    } finally {
      setScraping(false);
    }
  };

  const handleScrapeNews = async () => {
    setScrapingNews(true);
    try {
      const res = await axios.post(`${API}/scrape-news`);
      toast.success(res.data.message);
    } catch (e) {
      toast.error("Haberler çekilirken bir hata oluştu.");
    } finally {
      setScrapingNews(false);
    }
  };

  const handleOfficialWebsite = async (event) => {
    // If already have official website, go directly
    if (event.official_website) {
      window.open(event.official_website, "_blank");
      return;
    }

    // Fetch official URL from backend
    setLoadingWebsite((prev) => ({ ...prev, [event.id]: true }));
    try {
      const res = await axios.get(`${API}/events/${event.id}/official-url`);
      if (res.data.url) {
        window.open(res.data.url, "_blank");
        // Update local state
        setEvents((prev) =>
          prev.map((e) =>
            e.id === event.id ? { ...e, official_website: res.data.url } : e
          )
        );
      } else {
        toast.info("Resmi site bulunamadı.");
      }
    } catch (e) {
      toast.error("Site bilgisi alınamadı.");
    } finally {
      setLoadingWebsite((prev) => ({ ...prev, [event.id]: false }));
    }
  };

  const eventDates = events.reduce((acc, event) => {
    if (!event.start_date) return acc;
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : start;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      acc.push(new Date(d));
    }
    return acc;
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Tarih belirtilmemiş";
    return new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return "";
    const diff = new Date(dateStr) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return "Geçmiş";
    if (days === 0) return "Bugün";
    if (days === 1) return "Yarın";
    return `${days} gün`;
  };

  const getDaysColor = (dateStr) => {
    if (!dateStr) return "border-stone-300";
    const diff = new Date(dateStr) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return "border-stone-300 text-stone-400";
    if (days <= 7) return "border-[#1B4F72] text-[#1B4F72]";
    if (days <= 30) return "border-[#D4AF37] text-[#D4AF37]";
    return "border-stone-300";
  };

  return (
    <div data-testid="calendar-page" className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-24">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1B4F72] mb-3 block">
              Dünya Fuar Takvimi
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] text-stone-950 mb-4">
              Fuar Takvimi
            </h1>
            <p className="text-base md:text-lg text-stone-500 max-w-2xl">
              Dünya genelinde <span className="text-[#1B4F72] font-semibold">{events.length}+</span> fuar ve etkinlik.
              Veriler <span className="text-[#1B4F72] font-medium">EventsEye.com</span>'dan otomatik çekilmektedir.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleScrapeNews}
              disabled={scrapingNews}
              variant="outline"
              className="rounded-none border-stone-300 gap-2 font-mono text-[10px] uppercase tracking-widest"
              data-testid="scrape-news-btn"
            >
              {scrapingNews ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
              {scrapingNews ? "Çevriliyor..." : "Haberleri Çek"}
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={scraping}
              variant="outline"
              className="rounded-none border-stone-300 gap-2 font-mono text-[10px] uppercase tracking-widest"
              data-testid="refresh-events-btn"
            >
              {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {scraping ? "Güncelleniyor..." : "Fuarları Güncelle"}
            </Button>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-10" data-testid="events-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Fuar adı veya konu ile arayın..."
            className="pl-10 rounded-none border-stone-300 h-11"
            data-testid="events-search-input"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="rounded-none border-stone-300 h-11 gap-2 font-mono text-[10px] uppercase tracking-widest"
              data-testid="events-country-filter"
            >
              <Globe className="w-3.5 h-3.5" />
              {selectedCountry || "Tüm Ülkeler"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-none max-h-72 overflow-auto">
            <DropdownMenuItem
              onClick={() => handleCountrySelect("")}
              className="font-mono text-xs uppercase tracking-widest"
            >
              Tüm Ülkeler
            </DropdownMenuItem>
            {countries.map((c) => (
              <DropdownMenuItem
                key={c.country}
                onClick={() => handleCountrySelect(c.country)}
                className="font-mono text-xs uppercase tracking-widest"
              >
                {c.country} ({c.count})
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="submit"
          className="rounded-none h-11 px-6 bg-stone-900 text-white hover:bg-[#1B4F72] transition-colors font-mono text-xs uppercase tracking-widest"
          data-testid="events-search-btn"
        >
          Ara
        </Button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Calendar Widget */}
        <div className="lg:col-span-4">
          <div className="bg-white border border-stone-200 p-6 sticky top-24">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4">
              Takvim
            </h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              locale={tr}
              modifiers={{ event: eventDates }}
              modifiersStyles={{
                event: {
                  backgroundColor: "#1B4F72",
                  color: "white",
                  borderRadius: "2px",
                },
              }}
              className="rounded-none"
              data-testid="event-calendar"
            />
            <div className="mt-4 flex items-center gap-2 text-xs text-stone-500 font-mono">
              <div className="w-3 h-3 bg-[#1B4F72]" />
              <span>Fuar günleri</span>
            </div>
            <div className="mt-6 pt-4 border-t border-stone-200">
              <p className="font-mono text-[10px] uppercase tracking-widest text-stone-400 mb-2">Kaynak</p>
              <p className="text-sm text-stone-600">
                <a href="https://www.eventseye.com" target="_blank" rel="noopener noreferrer" className="text-[#1B4F72] hover:underline">EventsEye.com</a> — Dünya çapında fuar veritabanı
              </p>
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="lg:col-span-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6">
            {events.length} Etkinlik {selectedCountry && `— ${selectedCountry}`}
          </h3>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white border border-stone-200 p-6">
                  <div className="skeleton h-6 w-3/4 mb-3" />
                  <div className="skeleton h-4 w-1/2 mb-2" />
                  <div className="skeleton h-4 w-2/3" />
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16" data-testid="no-events">
              <p className="text-xl text-stone-400 mb-2">Etkinlik bulunamadı</p>
              <p className="text-stone-500">Farklı arama kriterleri deneyin.</p>
            </div>
          ) : (
            <div className="space-y-0" data-testid="events-list">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex flex-col md:flex-row md:items-start justify-between py-5 border-b border-stone-200 hover:bg-stone-50 px-4 -mx-4 transition-colors group"
                  data-testid={`event-${event.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-base md:text-lg font-semibold text-stone-900 group-hover:text-[#1B4F72] transition-colors">
                        {event.name}
                      </h3>
                      {event.start_date && getDaysUntil(event.start_date) && (
                        <Badge
                          variant="outline"
                          className={`font-mono text-[9px] uppercase tracking-widest ${getDaysColor(event.start_date)}`}
                        >
                          {getDaysUntil(event.start_date)}
                        </Badge>
                      )}
                      {event.approximate && (
                        <Badge variant="outline" className="font-mono text-[9px] uppercase tracking-widest border-amber-300 text-amber-600">
                          Tahmini
                        </Badge>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-sm text-stone-500 mb-2 line-clamp-2">
                        {event.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-stone-400">
                      {event.country && (
                        <span className="flex items-center gap-1 text-[#003049] font-medium">
                          <Globe className="w-3 h-3" />
                          {event.country}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.city}
                      </span>
                      {event.location && event.location !== event.city && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate max-w-[200px]">{event.location}</span>
                        </span>
                      )}
                      {event.start_date && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {formatDate(event.start_date)}
                          {event.end_date && event.end_date !== event.start_date && ` - ${formatDate(event.end_date)}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 md:mt-0 md:ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleOfficialWebsite(event)}
                      disabled={loadingWebsite[event.id]}
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-stone-400 hover:text-[#1B4F72] transition-colors disabled:opacity-50"
                      data-testid={`event-link-${event.id}`}
                    >
                      {loadingWebsite[event.id] ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <ExternalLink className="w-3 h-3" />
                      )}
                      {event.official_website ? "Resmi Site" : "Resmi Site"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
